import React, { useEffect, useState } from 'react';
import { Youtube, Calendar } from 'lucide-react';
import { getLiveStream } from '../lib/youtube';

interface LiveStream {
  title: string;
  description: string;
  youtube_id: string;
  scheduled_start: string;
  is_live: boolean;
}

export default function LiveStream() {
  const [stream, setStream] = useState<LiveStream | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function checkLiveStream() {
      const liveStream = await getLiveStream();
      setStream(liveStream);
      setIsLoading(false);
    }

    checkLiveStream();

    // Check for live stream every 5 minutes
    const interval = setInterval(checkLiveStream, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div id="live-stream" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-4">
            <div className="h-16 w-16 bg-gray-200 rounded-2xl"></div>
            <div className="h-6 bg-gray-200 rounded w-1/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-[400px] bg-gray-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="live-stream" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Live Stream</h2>
          <div className="w-24 h-1 bg-primary-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Join us live for our Sunday services and special events.
          </p>
        </div>

        {!stream ? (
          <div className="bg-white p-8 rounded-2xl shadow-sm text-center">
            <Youtube className="text-primary-600 mx-auto mb-4" size={48} />
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Service Information</h3>
            <p className="text-gray-600 mb-6">
              Join us for our weekly Sunday service at 1:30 PM EST.
            </p>
            <div className="inline-flex items-center bg-gray-50 px-6 py-3 rounded-xl shadow-sm">
              <Calendar className="text-primary-600 mr-2" size={20} />
              <span className="font-medium">Every Sunday at 1:30 PM EST</span>
            </div>
          </div>
        ) : (
          <div className="bg-white p-8 rounded-2xl shadow-sm">
            {stream.is_live && stream.youtube_id ? (
              <div>
                <div className="flex items-center mb-4">
                  <span className="flex h-3 w-3 relative mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                  <span className="text-red-600 font-semibold">LIVE NOW</span>
                </div>
                <div className="aspect-video rounded-xl overflow-hidden shadow-lg mb-4">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${stream.youtube_id}?autoplay=1&rel=0`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">{stream.title}</h4>
                <p className="text-gray-600">{stream.description}</p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-lg font-semibold mb-2">Next Service</p>
                <p className="text-gray-600">
                  {new Date(stream.scheduled_start).toLocaleDateString(undefined, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
                <p className="text-gray-600 mb-6">
                  {new Date(stream.scheduled_start).toLocaleTimeString(undefined, {
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </p>
                <a
                  href="https://www.youtube.com/@clevelandstreetpreachers"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-primary-600 text-white px-6 py-3 rounded-full hover:bg-primary-700 transition-colors"
                >
                  <Youtube className="mr-2" size={20} />
                  Visit Our Channel
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}